import { useEffect, useState } from "react";

import { Box, SimpleGrid, Text } from "@mantine/core";
import { UseFormReturnType } from "@mantine/form";
import { useTranslation } from "react-i18next";

import { TextInput } from "components";
import SectionTitle from "pages/clients/view/components/section-title";
import {
  comparisonValuesDiffer,
  formatComparisonDate,
  hasComparisonValue,
  normalizeComparisonValue,
} from "pages/clients/view/credit/request/preliminary-comparison";
import PreliminaryComparisonModal, {
  PreliminaryComparisonRow,
} from "pages/clients/view/credit/request/preliminary-comparison-modal";
import {
  CreditOptionsResponse,
  CreditRequestEmploymentResponse,
  CreditRequestForm,
  CreditRequestIncomeResponse,
  CreditResponse,
} from "types";

import { CreditComparisonSource } from "../comparison-source";
import CreditRequestEmployment from "./employment";
import CreditRequestIncome from "./income";

interface CreditRequestIncomesProps {
  credit: CreditResponse;
  form: UseFormReturnType<CreditRequestForm>;
  options: CreditOptionsResponse;
  readOnly?: boolean;
  comparisonSource?: CreditComparisonSource | null;
}

type WarningSection = "employment" | "income" | "partner-employment" | "partner-income";
type ComparisonFieldConfig<T> = {
  name: keyof T;
  label: string;
  formatValue?: (value: any) => string;
};
type MergeableSectionItem = {
  creditRequestId?: number;
  partner?: boolean;
};

const filterSectionValues = <T extends { partner?: boolean }>(
  values: T[] | undefined,
  partner: boolean,
) => (values ?? []).filter((item) => (partner ? item.partner === true : !item.partner));

const hasEmploymentData = (item: CreditRequestEmploymentResponse) =>
  hasComparisonValue(item.professionalSituation) ||
  hasComparisonValue(item.requesterPaysSalary) ||
  hasComparisonValue(item.salary) ||
  hasComparisonValue(item.thirteenthSalary) ||
  hasComparisonValue(item.thirteenthPension) ||
  hasComparisonValue(item.bonusReceived) ||
  hasComparisonValue(item.socialHelp) ||
  hasComparisonValue(item.employmentType) ||
  hasComparisonValue(item.contractStartDate) ||
  hasComparisonValue(item.contractEndDate) ||
  hasComparisonValue(item.travelingToWork);

const hasIncomeData = (item: CreditRequestIncomeResponse) =>
  hasComparisonValue(item.incomeType) ||
  hasComparisonValue(item.startDate) ||
  hasComparisonValue(item.amount);

const CreditRequestIncomes = ({
  credit,
  form,
  options,
  readOnly = false,
  comparisonSource,
}: CreditRequestIncomesProps) => {
  const { t } = useTranslation();
  const comparisonCredit = comparisonSource?.credit;
  const comparisonIndicatorType = comparisonSource?.type === "previous" ? "history" : "warning";
  const isPreviousComparison = comparisonSource?.type === "previous";
  const canApplyComparisonSource = !!comparisonSource;

  const [partnerIncludeInApplication, setPartnerIncludeInApplication] = useState<boolean | undefined>(
    credit.partnerIncludeInApplication,
  );
  const [activeWarningSection, setActiveWarningSection] = useState<WarningSection | null>(null);
  const [sectionSyncVersions, setSectionSyncVersions] = useState<Record<WarningSection, number>>({
    employment: 0,
    income: 0,
    "partner-employment": 0,
    "partner-income": 0,
  });

  const cloneEmploymentSection = (values: CreditRequestEmploymentResponse[], partner: boolean) =>
    values.map((item) => {
      const { id, creditRequestId, ...rest } = item;

      return {
        ...rest,
        creditRequestId: credit.id,
        partner,
        contractStartDate: item.contractStartDate ? new Date(item.contractStartDate) : undefined,
        contractEndDate: item.contractEndDate ? new Date(item.contractEndDate) : undefined,
      };
    });

  const mergeSectionValues = <T extends MergeableSectionItem>(
    activeValues: T[],
    preliminaryValues: T[],
  ) => {
    const nextValues = activeValues.map((item) => ({ ...item }));

    preliminaryValues.forEach((preliminaryItem, index) => {
      if (nextValues[index]) {
        nextValues[index] = {
          ...nextValues[index],
          ...preliminaryItem,
        };
        return;
      }

      nextValues.push({ ...preliminaryItem });
    });

    return nextValues;
  };

  const cloneIncomeSection = (values: CreditRequestIncomeResponse[], partner: boolean) =>
    values.map((item) => {
      const { id, creditRequestId, ...rest } = item;

      return {
        ...rest,
        creditRequestId: credit.id,
        partner,
        startDate: item.startDate ? new Date(item.startDate) : undefined,
      };
    });

  useEffect(() => {
    const formValues = form.getValues();
    if (Object.keys(formValues).length === 0) {
      return;
    }
    const include = formValues.partnerIncludeInApplication;
    if (!include) {
      const employments = credit["employments"] ? credit["employments"].filter((el) => !el.partner) : [];
      const incomes = credit["incomes"] ? credit["incomes"].filter((el) => !el.partner) : [];
      form.setFieldValue("employments", employments);
      form.setFieldValue("incomes", incomes);
    }
    setPartnerIncludeInApplication(include);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.getValues().partnerIncludeInApplication]);

  const currentValues = form.getValues();
  const currentEmploymentSection = filterSectionValues(currentValues.employments, false);
  const currentIncomeSection = filterSectionValues(currentValues.incomes, false);
  const currentPartnerEmploymentSection = filterSectionValues(currentValues.employments, true);
  const currentPartnerIncomeSection = filterSectionValues(currentValues.incomes, true);

  const comparisonEmploymentSection = filterSectionValues(comparisonCredit?.employments, false);
  const comparisonIncomeSection = filterSectionValues(comparisonCredit?.incomes, false);
  const comparisonPartnerEmploymentSection = filterSectionValues(comparisonCredit?.employments, true);
  const comparisonPartnerIncomeSection = filterSectionValues(comparisonCredit?.incomes, true);

  const formatYesNoValue = (value: any) =>
    value === true || value === 1 ? t("yes") : value === false || value === 0 ? t("no") : "-";

  const employmentFields: ComparisonFieldConfig<CreditRequestEmploymentResponse>[] = [
    {
      name: "professionalSituation",
      label: t("credit.employment.professionalSituation.label"),
      formatValue: (value) => (value ? t(`credit.employment.professionalSituation.options.${value}`) : "-"),
    },
    {
      name: "salary",
      label: t("credit.employment.salary.label"),
      formatValue: (value) => String(normalizeComparisonValue(value) ?? "-"),
    },
    {
      name: "requesterPaysSalary",
      label: t("credit.employment.requesterPaysSalary.label"),
      formatValue: formatYesNoValue,
    },
    {
      name: "thirteenthSalary",
      label: t("credit.employment.thirteenthSalary.label"),
      formatValue: formatYesNoValue,
    },
    {
      name: "thirteenthPension",
      label: t("credit.employment.thirteenthPension.label"),
      formatValue: formatYesNoValue,
    },
    {
      name: "bonusReceived",
      label: t("credit.employment.bonusReceived.label"),
      formatValue: formatYesNoValue,
    },
    {
      name: "socialHelp",
      label: t("credit.employment.socialHelp.label"),
      formatValue: formatYesNoValue,
    },
    {
      name: "employmentType",
      label: t("credit.employment.employmentType.label"),
      formatValue: (value) => (value ? t(`credit.employment.employmentType.options.${value}`) : "-"),
    },
    {
      name: "contractStartDate",
      label: t("credit.employment.contractStartDate.label"),
      formatValue: (value) => formatComparisonDate(value, "month"),
    },
    {
      name: "contractEndDate",
      label: t("credit.employment.contractEndDate.label"),
      formatValue: (value) => formatComparisonDate(value, "month"),
    },
    {
      name: "travelingToWork",
      label: t("credit.employment.travelingToWork.label"),
      formatValue: (value) => (value ? t(`credit.employment.travelingToWork.options.${value}`) : "-"),
    },
  ];

  const incomeFields: ComparisonFieldConfig<CreditRequestIncomeResponse>[] = [
    {
      name: "incomeType",
      label: t("credit.income.incomeType.label"),
      formatValue: (value) => (value ? t(`credit.income.incomeType.options.${value}`) : "-"),
    },
    {
      name: "amount",
      label: t("credit.income.amount.label"),
      formatValue: (value) => String(normalizeComparisonValue(value) ?? "-"),
    },
    {
      name: "startDate",
      label: t("credit.income.startDate.label"),
      formatValue: (value) => formatComparisonDate(value, "month"),
    },
  ];

  const buildArrayRows = <T extends Record<string, any>>(
    sourceValues: T[],
    activeValues: T[],
    fields: ComparisonFieldConfig<T>[],
    sectionKey: WarningSection,
  ): PreliminaryComparisonRow[] => {
    const maxLength = Math.max(sourceValues.length, activeValues.length);

    return Array.from({ length: maxLength }).flatMap((_, index) =>
      fields
        .filter((field) =>
          comparisonValuesDiffer(
            sourceValues[index]?.[field.name as string],
            activeValues[index]?.[field.name as string],
          ),
        )
        .map((field) => {
          const formatValue =
            field.formatValue ?? ((value: any) => String(normalizeComparisonValue(value) ?? "-"));
          const label = maxLength > 1 ? `${index + 1}. ${field.label}` : field.label;

          return {
            key: `${sectionKey}-${index}-${String(field.name)}`,
            label,
            source: <Text>{formatValue(sourceValues[index]?.[field.name as string])}</Text>,
            current: <Text>{formatValue(activeValues[index]?.[field.name as string])}</Text>,
          };
        }),
    );
  };

  const employmentWarning =
    !!comparisonCredit &&
    (isPreviousComparison
      ? comparisonEmploymentSection.some(hasEmploymentData)
      : currentEmploymentSection.some(hasEmploymentData)) &&
    comparisonValuesDiffer(
      currentEmploymentSection.map((item) =>
        employmentFields.reduce(
          (acc, field) => ({ ...acc, [field.name]: item[field.name] }),
          {} as Record<string, any>,
        ),
      ),
      comparisonEmploymentSection.map((item) =>
        employmentFields.reduce(
          (acc, field) => ({ ...acc, [field.name]: item[field.name] }),
          {} as Record<string, any>,
        ),
      ),
    );

  const incomeWarning =
    !!comparisonCredit &&
    (isPreviousComparison
      ? comparisonIncomeSection.some(hasIncomeData)
      : currentIncomeSection.some(hasIncomeData)) &&
    comparisonValuesDiffer(
      currentIncomeSection.map((item) =>
        incomeFields.reduce(
          (acc, field) => ({ ...acc, [field.name]: item[field.name] }),
          {} as Record<string, any>,
        ),
      ),
      comparisonIncomeSection.map((item) =>
        incomeFields.reduce(
          (acc, field) => ({ ...acc, [field.name]: item[field.name] }),
          {} as Record<string, any>,
        ),
      ),
    );

  const partnerEmploymentWarning =
    !!comparisonCredit &&
    (isPreviousComparison
      ? comparisonPartnerEmploymentSection.some(hasEmploymentData)
      : currentPartnerEmploymentSection.some(hasEmploymentData)) &&
    comparisonValuesDiffer(
      currentPartnerEmploymentSection.map((item) =>
        employmentFields.reduce(
          (acc, field) => ({ ...acc, [field.name]: item[field.name] }),
          {} as Record<string, any>,
        ),
      ),
      comparisonPartnerEmploymentSection.map((item) =>
        employmentFields.reduce(
          (acc, field) => ({ ...acc, [field.name]: item[field.name] }),
          {} as Record<string, any>,
        ),
      ),
    );

  const partnerIncomeWarning =
    !!comparisonCredit &&
    (isPreviousComparison
      ? comparisonPartnerIncomeSection.some(hasIncomeData)
      : currentPartnerIncomeSection.some(hasIncomeData)) &&
    comparisonValuesDiffer(
      currentPartnerIncomeSection.map((item) =>
        incomeFields.reduce(
          (acc, field) => ({ ...acc, [field.name]: item[field.name] }),
          {} as Record<string, any>,
        ),
      ),
      comparisonPartnerIncomeSection.map((item) =>
        incomeFields.reduce(
          (acc, field) => ({ ...acc, [field.name]: item[field.name] }),
          {} as Record<string, any>,
        ),
      ),
    );

  const comparisonRows = (() => {
    if (!activeWarningSection || !comparisonCredit) {
      return [];
    }

    switch (activeWarningSection) {
      case "employment":
        return buildArrayRows(
          comparisonEmploymentSection,
          currentEmploymentSection,
          employmentFields,
          activeWarningSection,
        );
      case "income":
        return buildArrayRows(
          comparisonIncomeSection,
          currentIncomeSection,
          incomeFields,
          activeWarningSection,
        );
      case "partner-employment":
        return buildArrayRows(
          comparisonPartnerEmploymentSection,
          currentPartnerEmploymentSection,
          employmentFields,
          activeWarningSection,
        );
      case "partner-income":
        return buildArrayRows(
          comparisonPartnerIncomeSection,
          currentPartnerIncomeSection,
          incomeFields,
          activeWarningSection,
        );
      default:
        return [];
    }
  })();

  const applyComparisonValues = () => {
    if (!activeWarningSection || !comparisonCredit) {
      return;
    }

    if (activeWarningSection === "employment" || activeWarningSection === "partner-employment") {
      const partner = activeWarningSection === "partner-employment";
      const comparisonSectionValues = cloneEmploymentSection(
        filterSectionValues(comparisonCredit.employments, partner),
        partner,
      );
      const activeSectionValues = filterSectionValues(currentValues.employments, partner);
      const mergedSectionValues = mergeSectionValues(
        activeSectionValues as any,
        comparisonSectionValues as any,
      );
      const remainingValues = filterSectionValues(currentValues.employments, !partner).map((item) => ({
        ...item,
      }));

      form.setFieldValue("employments", [...remainingValues, ...mergedSectionValues] as any);
    }

    if (activeWarningSection === "income" || activeWarningSection === "partner-income") {
      const partner = activeWarningSection === "partner-income";
      const comparisonSectionValues = cloneIncomeSection(
        filterSectionValues(comparisonCredit.incomes, partner),
        partner,
      );
      const activeSectionValues = filterSectionValues(currentValues.incomes, partner);
      const mergedSectionValues = mergeSectionValues(
        activeSectionValues as any,
        comparisonSectionValues as any,
      );
      const remainingValues = filterSectionValues(currentValues.incomes, !partner).map((item) => ({
        ...item,
      }));

      form.setFieldValue("incomes", [...remainingValues, ...mergedSectionValues] as any);
    }

    setSectionSyncVersions((prev) => ({
      ...prev,
      [activeWarningSection]: prev[activeWarningSection] + 1,
    }));
    setActiveWarningSection(null);
  };

  return (
    <Box p={"md"}>
      <Box mb={40}>
        <SectionTitle
          label={t("credit.employment.title")}
          hasWarning={employmentWarning}
          indicatorType={comparisonIndicatorType}
          onWarningClick={() => setActiveWarningSection("employment")}
        />
        <CreditRequestEmployment
          credit={credit}
          form={form}
          options={options}
          partner={false}
          readOnly={readOnly}
          syncValues={currentEmploymentSection}
          syncVersion={sectionSyncVersions.employment}
        />
      </Box>
      <Box mt={"md"} mb={40}>
        <CreditRequestIncome
          credit={credit}
          form={form}
          options={options}
          partner={false}
          readOnly={readOnly}
          hasWarning={incomeWarning}
          indicatorType={comparisonIndicatorType}
          onWarningClick={() => setActiveWarningSection("income")}
          syncValues={currentIncomeSection}
          syncVersion={sectionSyncVersions.income}
        />
      </Box>
      {partnerIncludeInApplication && (
        <>
          <Box mt={"md"} mb={40}>
            <SectionTitle
              label={t("credit.employment.partner.title")}
              hasWarning={partnerEmploymentWarning}
              indicatorType={comparisonIndicatorType}
              onWarningClick={() => setActiveWarningSection("partner-employment")}
            />
            <CreditRequestEmployment
              credit={credit}
              form={form}
              options={options}
              partner={true}
              readOnly={readOnly}
              syncValues={currentPartnerEmploymentSection}
              syncVersion={sectionSyncVersions["partner-employment"]}
            />
          </Box>
          <Box mt={"md"} mb={40}>
            <CreditRequestIncome
              credit={credit}
              form={form}
              options={options}
              partner={true}
              readOnly={readOnly}
              hasWarning={partnerIncomeWarning}
              indicatorType={comparisonIndicatorType}
              onWarningClick={() => setActiveWarningSection("partner-income")}
              syncValues={currentPartnerIncomeSection}
              syncVersion={sectionSyncVersions["partner-income"]}
            />
          </Box>

          <Box mt={"md"}>
            <SimpleGrid cols={4}>
              <TextInput
                label={t("credit.comment.singular")}
                key={form.key("comment3")}
                {...form.getInputProps("comment3")}
                disabled={readOnly}
              />
            </SimpleGrid>
          </Box>
        </>
      )}

      <PreliminaryComparisonModal
        opened={activeWarningSection !== null}
        onClose={() => setActiveWarningSection(null)}
        onApply={applyComparisonValues}
        title={t(
          comparisonSource?.type === "previous"
            ? "credit.previousComparison.title"
            : "credit.preliminaryComparison.title",
        )}
        emptyText={t("credit.preliminaryComparison.empty")}
        fieldLabel={t("credit.preliminaryComparison.field")}
        sourceLabel={t(
          comparisonSource?.type === "previous"
            ? "credit.previousComparison.previous"
            : "credit.preliminaryComparison.preliminary",
        )}
        currentLabel={t("credit.preliminaryComparison.active")}
        applyLabel={t(
          comparisonSource?.type === "previous"
            ? "credit.previousComparison.apply"
            : "credit.preliminaryComparison.apply",
        )}
        cancelLabel={t("credit.preliminaryComparison.cancel")}
        rows={comparisonRows}
        showApplyButton={canApplyComparisonSource}
      />
    </Box>
  );
};

export default CreditRequestIncomes;
